import { useEffect, useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { useAssignCommonResult } from "features/results/hooks/useAssignCommonResult";

import styles from "./UnmatchedAssignSection.module.css";

const getPatientName = (patient) => {
  const name = `${patient?.firstName ?? ""} ${patient?.surname ?? ""}`.trim();

  return name || null;
};

/**
 * Which patient a result is filed under — set it, or correct it.
 *
 * Correcting it matters because the matching is done on the message's own
 * demographics and can be wrong: a discharge summary has been filed against a
 * patient who merely shared a name, with a completely different date of birth. Until
 * now the front end offered the picker only for a result with no patient at all, so a
 * result matched to the wrong one had no way back — even though the backend has always
 * allowed it (`addPatientToMessage` overwrites `patient_id` unconditionally, and
 * re-points the lab order for managed types).
 *
 * Moving one is deliberately slower than assigning one: the picker is behind a button
 * and the move behind a confirmation that names both patients, because this takes a
 * clinical document off somebody's chart.
 */
export const UnmatchedAssignSection = ({ item, onAssigned }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // All result types are served by the common endpoint, so a single assign
  // mutation (which invalidates the common results lists) covers every tab.
  const { mutate: assign, isPending } = useAssignCommonResult();

  const currentPatientName = getPatientName(item?.patient);
  const isFiled = !!(item?.patientId || item?.patient);

  useEffect(() => {
    setSelectedPatient(null);
    setIsPickerOpen(false);
    setIsConfirmOpen(false);
  }, [item?.id]);

  const handleAssign = () => {
    if (!selectedPatient?.id) return;

    const payload = { resultId: item.id, patientId: selectedPatient.id };
    // Report the assigned patient back so the caller keeps the result preview
    // open (reflecting the new assignment) instead of clearing it.
    assign(payload, {
      onSuccess: () => {
        setIsConfirmOpen(false);
        setIsPickerOpen(false);
        onAssigned?.(selectedPatient);
      },
    });
  };

  // Just the way out. Who the result is filed under is already named at the top of
  // the panel, so repeating it here would be the second answer to the same question.
  if (isFiled && !isPickerOpen) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.filedRow}>
          {/* The document's Print button lands here rather than on a line of its own above the
              document — the two are the panel's actions and belong in one row (KIN-1979). */}
          <span data-healthlink-toolbar-slot className={styles.toolbarSlot} />
          <Button variant="outline" onClick={() => setIsPickerOpen(true)}>
            Move to another patient
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <p className={styles.label}>
        {isFiled ? "Move to another patient" : "Assign to patient"}
      </p>

      <div className={styles.controls}>
        <div className={styles.autocomplete}>
          <PatientAutocomplete
            value={selectedPatient}
            onChange={setSelectedPatient}
          />
        </div>

        <Button
          variant="primary"
          className={styles.confirmButton}
          disabled={!selectedPatient?.id || isPending}
          onClick={() => (isFiled ? setIsConfirmOpen(true) : handleAssign())}
        >
          {isFiled ? "Move result" : "Confirm assignment"}
        </Button>

        {!!isFiled && (
          <Button
            variant="outline"
            className={styles.confirmButton}
            onClick={() => {
              setIsPickerOpen(false);
              setSelectedPatient(null);
            }}
          >
            Cancel
          </Button>
        )}
      </div>

      <Dialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Move this result?"
        width={520}
      >
        <div className={styles.confirmBody}>
          <p className={styles.confirmText}>
            This takes the result off{" "}
            <strong>{currentPatientName ?? "the current patient"}</strong>&apos;s
            chart and files it under{" "}
            <strong>{selectedPatient?.title ?? "the selected patient"}</strong>.
          </p>
          <p className={styles.confirmNote}>
            Anything already created from it on the old chart stays there.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={isPending} onClick={handleAssign}>
            {isPending ? "Moving..." : "Move result"}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};
